import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Plus, Edit2, Trash2, LogOut, LayoutDashboard, MapPin, TrendingUp, Users, Eye, EyeOff, Globe, Database, RefreshCcw, QrCode, ChevronDown, ChevronRight, FileText } from 'lucide-react';
import Button from '../../components/ui/Button';
import { supabase } from '../../lib/supabase';
import { AGENT_INFO } from '../../constants';
import { CITY_COORDINATES } from '../../data/geocoding';
import ShareModal from '../../components/ShareModal';
import PosterModal from '../../components/PosterModal';

// ─── Libellés et couleurs par site_target ────────────────────────────────────
const DESTINATION_CONFIG = {
    vanessa: { label: 'Ottange', color: 'bg-orange-100 text-orange-700 border-orange-200' },
    scbi: { label: 'Ottange', color: 'bg-orange-100 text-orange-700 border-orange-200' },
    avantis: { label: 'Avantis', color: 'bg-green-100 text-green-700 border-green-200' },
};

const DestinationBadge = ({ target }) => {
    const cfg = DESTINATION_CONFIG[target] ?? { label: target ?? '?', color: 'bg-gray-100 text-gray-500 border-gray-200' };
    return (
        <span className={`inline-block px-2 py-0.5 rounded border text-[9px] font-black uppercase tracking-wider ${cfg.color}`}>
            {cfg.label}
        </span>
    );
};

const ToggleSwitch = ({ active, onClick, labelOn = 'En Ligne', labelOff = 'Masqué' }) => {
    return (
        <div
            onClick={onClick}
            className="flex items-center gap-3 cursor-pointer group select-none"
        >
            <span className={`text-[9px] font-black uppercase tracking-widest transition-colors ${active ? 'text-green-600' : 'text-gray-400'}`}>
                {active ? labelOn : labelOff}
            </span>
            <div className={`relative w-10 h-5 rounded-full transition-all duration-300 ${active ? 'bg-green-500 shadow-sm shadow-green-200' : 'bg-gray-200'}`}>
                <div className={`absolute top-1 left-1 w-3 h-3 bg-white rounded-full transition-transform duration-300 shadow-sm ${active ? 'translate-x-5' : 'translate-x-0'}`} />
            </div>
        </div>
    );
};

// ─── Composant principal ──────────────────────────────────────────────────────
const Dashboard = () => {
    const navigate = useNavigate();
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({ visitors24h: 0, views24h: 0, visitors7d: 0, visitors30d: 0 });
    const [notTrackingMe, setNotTrackingMe] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    const [shareModalConfig, setShareModalConfig] = useState({ title: '', url: '' });
    const [posterProperty, setPosterProperty] = useState(null);
    const [isPosterModalOpen, setIsPosterModalOpen] = useState(false);
    const siteTarget = import.meta.env.VITE_SITE_TARGET || 'scbi';
    const publishedKey = `is_published_${siteTarget}`;

    // Gestion des villes
    const [cities, setCities] = useState([]);

    // Filtre actif : 'all' | 'vanessa' | 'scbi' | 'avantis'
    const [destinationFilter, setDestinationFilter] = useState(siteTarget);
    // Filtre agent secondaire (actif lorsque destinationFilter === 'scbi')
    const [selectedAgentFilter, setSelectedAgentFilter] = useState(null);

    // Biens d'Ottange (scbi + vanessa)
    const ottangeProperties = useMemo(() => {
        return properties.filter(p => p.site_target === 'scbi' || p.site_target === 'vanessa');
    }, [properties]);

    // Liste unique des agents avec leur nombre de biens
    const availableAgentsWithCounts = useMemo(() => {
        const countsMap = {};
        for (const p of ottangeProperties) {
            const rawName = (p.agent_firstname || '').trim();
            const name = rawName 
                ? rawName.charAt(0).toUpperCase() + rawName.slice(1).toLowerCase()
                : 'Non assigné';
            countsMap[name] = (countsMap[name] || 0) + 1;
        }
        return Object.entries(countsMap)
            .map(([name, count]) => ({ name, count }))
            .sort((a, b) => a.name.localeCompare(b.name));
    }, [ottangeProperties]);

    const handleSetDestinationFilter = (tabKey) => {
        setDestinationFilter(tabKey);
        if (tabKey !== 'scbi') {
            setSelectedAgentFilter(null);
        }
    };

    const handleToggleAgentFilter = (agentName) => {
        if (selectedAgentFilter === agentName) {
            setSelectedAgentFilter(null);
        } else {
            setSelectedAgentFilter(agentName);
        }
    };

    const [syncing, setSyncing] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState({});

    const toggleGroupExpand = (groupKey) => {
        setExpandedGroups(prev => ({
            ...prev,
            [groupKey]: !prev[groupKey]
        }));
    };

    useEffect(() => {
        fetchAllNettyProperties();
        fetchStats();
        fetchCities();
        setNotTrackingMe(localStorage.getItem('admin_opt_out') === 'true');
    }, []);

    const handleSync = async () => {
        setSyncing(true);
        try {
            const { triggerNettySync } = await import('../../lib/nettyService');
            const result = await triggerNettySync();
            alert(`${result.synced} Biens ont été trouvés sur la passerelle KauBryApps.`);
            fetchAllNettyProperties();
        } catch (err) {
            alert('Erreur lors de la synchronisation : ' + (err.message || String(err)));
        } finally {
            setSyncing(false);
        }
    };

    const fetchCities = async () => {
        const { data } = await supabase.from('cities').select('*').order('name');
        if (data) {
            setCities(data);
        }
    };

    const toggleOptOut = () => {
        const newValue = !notTrackingMe;
        setNotTrackingMe(newValue);
        localStorage.setItem('admin_opt_out', String(newValue));
        alert(newValue ? 'Vos visites ne seront plus comptées.' : 'Vos visites sont de nouveau comptées.');
    };

    const fetchStats = async () => {
        try {
            const { data, error } = await supabase.rpc('get_dashboard_stats', { p_site_target: siteTarget });
            if (!error && data) {
                setStats({
                    visitors24h: data.visitors24h || 0,
                    views24h: data.views24h || 0,
                    visitors7d: data.visitors7d || 0,
                    visitors30d: data.visitors30d || 0
                });
            }
        } catch { }
    };

    // L'admin récupère tous les biens (Netty + Avantis)
    const fetchAllNettyProperties = async () => {
        setLoading(true);
        const { data } = await supabase
            .from('properties')
            .select('*')
            .order('created_at', { ascending: false });
        if (data) setProperties(data);
        setLoading(false);
    };

    // ─── Filtrage côté front ───────────────────────────────────────────────────
    const filteredProperties = useMemo(() => {
        let result = properties;
        if (destinationFilter === 'scbi') {
            result = ottangeProperties;
            if (selectedAgentFilter) {
                result = result.filter(p => {
                    const rawName = (p.agent_firstname || '').trim();
                    const name = rawName 
                        ? rawName.charAt(0).toUpperCase() + rawName.slice(1).toLowerCase()
                        : 'Non assigné';
                    return name === selectedAgentFilter;
                });
            }
        } else if (destinationFilter !== 'all') {
            result = properties.filter(p => p.site_target === destinationFilter);
        }
        return result;
    }, [properties, ottangeProperties, destinationFilter, selectedAgentFilter]);

    // Group properties into projects (collapsible programs) using title prefixes and coordinates
    const groupedItems = useMemo(() => {
        const cleanTitleForProject = (title) => {
            if (!title) return "";
            let cleaned = title.replace(/[–—]/g, "-");
            cleaned = cleaned.replace(/\s*-\s*(?:Bâtiment\s+\w+|Lot\s+(?:n°)?\w+|n°\s*\w+|Appartement\s+[TF]\d+|[TF]\d+).*/gi, "");
            cleaned = cleaned.replace(/\s*(?:Bâtiment\s+\w+|Lot\s+(?:n°)?\w+|n°\s*\w+|Appartement\s+[TF]\d+|[TF]\d+).*/gi, "");
            cleaned = cleaned.replace(/\s*(?:lot|bâtiment|batiment|appt|apprt|appart)\s*$/gi, "");
            return cleaned.trim();
        };

        const hasLotIndicator = (title) => {
            if (!title) return false;
            return /\b(lot|batiment|bâtiment|bat|app|bloc|escalier|n°|t\d+|f\d+)\b/i.test(title);
        };

        const normalize = (str) => {
            if (!str) return "";
            return str
                .toLowerCase()
                .normalize("NFD")
                .replace(/[\u0300-\u036f]/g, "")
                .replace(/[^a-z0-9]/g, " ")
                .replace(/\s+/g, " ")
                .trim();
        };

        const list = [];
        const projectGroups = new Map();

        for (const prop of filteredProperties) {
            const isLot = hasLotIndicator(prop.title);
            const cleanTitle = cleanTitleForProject(prop.title);
            const normTitle = normalize(cleanTitle);
            const cityNorm = normalize(prop.city);
            
            const lat = prop.latitude;
            const lon = prop.longitude;
            const cityKey = (prop.city || "").toLowerCase().trim();
            const cityCenter = CITY_COORDINATES[cityKey] || CITY_COORDINATES[cityKey.replace(/\s+/g, '-')] || null;
            const isFallbackGPS = cityCenter && Math.abs(cityCenter[0] - lat) < 0.00001 && Math.abs(cityCenter[1] - lon) < 0.00001;
            const hasGPS = lat && lon && lat !== 0 && lon !== 0 && !isFallbackGPS;

            let groupKey = null;
            const propStatus = prop.status || 'Vente';

            // Try to find matching group key
            for (const [key, val] of projectGroups.entries()) {
                if (val.status !== propStatus) {
                    continue;
                }

                // Criterion 1: GPS Match
                if (hasGPS && val.gps && Math.abs(val.gps.lat - lat) < 0.00005 && Math.abs(val.gps.lon - lon) < 0.00005) {
                    if (isLot || val.hasLotIndicator) {
                        groupKey = key;
                        break;
                    }
                }
                // Criterion 2: Title and City match, with lot indicator
                if (normTitle && val.normalizedTitle === normTitle && val.cityNorm === cityNorm) {
                    if (isLot || val.hasLotIndicator) {
                        groupKey = key;
                        break;
                    }
                }
            }

            if (groupKey) {
                const grp = projectGroups.get(groupKey);
                grp.properties.push(prop);
                if (isLot) grp.hasLotIndicator = true;
            } else {
                const newKey = prop.id || prop.netty_ref;
                projectGroups.set(newKey, {
                    key: newKey,
                    title: cleanTitle || prop.title,
                    normalizedTitle: normTitle,
                    cityNorm,
                    gps: hasGPS ? { lat, lon } : null,
                    hasLotIndicator: isLot,
                    status: propStatus,
                    properties: [prop]
                });
            }
        }

        // Convert groups map to list (grouped under a program if > 1 properties)
        for (const grp of projectGroups.values()) {
            if (grp.properties.length > 1) {
                // Sort properties inside the group alphabetically and numerically (natural sort)
                const sortedProps = [...grp.properties].sort((a, b) => 
                    (a.title || "").localeCompare(b.title || "", undefined, { numeric: true, sensitivity: 'base' })
                );

                list.push({
                    isGroup: true,
                    key: grp.key,
                    title: grp.title,
                    city: sortedProps[0].city,
                    status: sortedProps[0].status,
                    properties: sortedProps,
                    minPrice: Math.min(...sortedProps.map(p => p.price)),
                    maxPrice: Math.max(...sortedProps.map(p => p.price)),
                });
            } else {
                list.push({
                    isGroup: false,
                    key: grp.properties[0].id,
                    property: grp.properties[0]
                });
            }
        }

        return list;
    }, [filteredProperties]);

    // Moment de la dernière synchronisation réussie (max des synced_at)
    const lastSyncTime = useMemo(() => {
        const syncDates = properties
            .filter(p => p.netty_ref && p.synced_at)
            .map(p => new Date(p.synced_at).getTime());
        return syncDates.length > 0 ? Math.max(...syncDates) : null;
    }, [properties]);

    const formattedLastSync = useMemo(() => {
        if (!lastSyncTime) return null;
        const d = new Date(lastSyncTime);
        const now = new Date();
        const isToday = d.toDateString() === now.toDateString();
        const timeStr = d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
        if (isToday) {
            return `Aujourd'hui à ${timeStr}`;
        }
        const yesterday = new Date(now);
        yesterday.setDate(yesterday.getDate() - 1);
        if (d.toDateString() === yesterday.toDateString()) {
            return `Hier à ${timeStr}`;
        }
        const dateStr = d.toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });
        return `Le ${dateStr} à ${timeStr}`;
    }, [lastSyncTime]);

    // ─── Compteurs par destination ─────────────────────────────────────────────
    const counts = useMemo(() => ({
        all: properties.length,
        scbi: properties.filter(p => p.site_target === 'scbi' || p.site_target === 'vanessa').length,
        avantis: properties.filter(p => p.site_target === 'avantis').length,
    }), [properties]);

    const pendingCount = properties.filter(p => !p[publishedKey]).length;
    const onlineCount = properties.filter(p => p[publishedKey]).length;

    // ─── Actions ──────────────────────────────────────────────────────────────
    const handleDelete = async (id) => {
        if (window.confirm('Êtes-vous sûre de vouloir supprimer ce bien ?')) {
            await supabase.from('properties').delete().eq('id', id);
            fetchAllNettyProperties();
        }
    };

    /**
     * Publier / Masquer un bien.
     */
    const handleTogglePublish = async (property) => {
        const willPublish = !property[publishedKey];

        if (willPublish) {
            const cityName = property.city?.trim();
            const exists = cities.some(c => c.name.toLowerCase() === cityName.toLowerCase());

            if (!exists) {
                // Création automatique de la ville si inexistante
                try {
                    const { data: newCity, error: cityError } = await supabase
                        .from('cities')
                        .insert([{ name: cityName, sector: 'Ottange' }]) // Valeur par défaut pour le schéma
                        .select()
                        .single();

                    if (!cityError && newCity) {
                        setCities(prev => [...prev, newCity]);
                    }
                } catch (err) {
                    console.error('Erreur création automatique ville:', err);
                }
            }
        }

        executePublish(property.id, willPublish);
    };

    const executePublish = async (propertyId, isPublished) => {
        const updatePayload = { [publishedKey]: isPublished };
        // Isolation totale : on ne touche plus au flag global is_published

        const { error } = await supabase
            .from('properties')
            .update(updatePayload)
            .eq('id', propertyId);

        if (!error) {
            setProperties(prev => prev.map(p =>
                p.id === propertyId ? { ...p, ...updatePayload } : p
            ));
        }
    };

    const handleToggleFavorite = async (id) => {
        const target = properties.find(p => p.id === id);
        const on = !target.is_favorite;
        if (on) await supabase.from('properties').update({ is_favorite: false }).neq('id', id);
        await supabase.from('properties').update({ is_favorite: on }).eq('id', id);
        fetchAllNettyProperties();
    };

    const handleLogout = async () => {
        await supabase.auth.signOut();
        navigate('/admin/login');
    };

    return (
        <div className="min-h-screen bg-gray-50 font-sans">

            {/* ── Navbar ─────────────────────────────────────────────────── */}
            <div className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-30 shadow-sm">
                <div className="max-w-7xl mx-auto flex justify-between items-center">
                    <div className="flex items-center gap-4">
                        <div className="bg-[#2C353D] text-white p-2 rounded-lg">
                            <LayoutDashboard size={24} />
                        </div>
                        <div>
                            <h1 className="font-black text-[#2C353D] text-xl leading-none">Espace Ottange</h1>
                            <p className="text-xs text-gray-400 font-bold uppercase tracking-wider">Gestion Agence Borbiconi</p>
                        </div>
                    </div>
                    <div className="flex gap-4 items-center">
                        <button onClick={toggleOptOut} className={`hidden md:flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold uppercase border transition-all ${notTrackingMe ? 'bg-red-50 text-red-500 border-red-100' : 'bg-green-50 text-green-600 border-green-100'}`}>
                            {notTrackingMe ? <EyeOff size={16} /> : <Eye size={16} />}
                            {notTrackingMe ? 'Tracking Désactivé' : 'Tracking Activé'}
                        </button>
                        <Link to="/" target="_blank">
                            <Button variant="ghost" className="hidden md:flex">Voir le site</Button>
                        </Link>
                        <button onClick={handleLogout} className="text-gray-400 hover:text-red-500 transition-colors p-2 hover:bg-gray-100 rounded-lg">
                            <LogOut size={24} />
                        </button>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 py-8">

                {/* ── Stat Cards ──────────────────────────────────────────── */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
                    <div className={`p-6 rounded-2xl border shadow-sm flex items-center justify-between transition-all ${pendingCount > 0 ? 'bg-orange-50 border-orange-200 animate-pulse' : 'bg-white border-gray-100'}`}>
                        <div>
                            <p className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-1">À Modérer (Modelo)</p>
                            <p className={`text-3xl font-black ${pendingCount > 0 ? 'text-orange-600' : 'text-[#2C353D]'}`}>{pendingCount}</p>
                            <p className="text-xs text-gray-400 mt-1">Nouveaux imports</p>
                        </div>
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${pendingCount > 0 ? 'bg-orange-100 text-orange-600' : 'bg-blue-50 text-[#2C353D]'}`}>
                            <Database size={24} />
                        </div>
                    </div>

                    <div className="bg-gradient-to-br from-[#EA723D] to-[#d66535] p-6 rounded-2xl shadow-lg text-white flex items-center justify-between">
                        <div>
                            <p className="text-white/60 text-xs font-bold uppercase tracking-wider mb-1">En Ligne (Total)</p>
                            <p className="text-3xl font-black">{onlineCount}</p>
                            <p className="text-xs text-white/40 mt-1">Total MODELO : {properties.length}</p>
                        </div>
                        <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center"><Globe size={24} /></div>
                    </div>
                </div>

                {/* ── QR Codes & Share Panel ── */}
                <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm mb-10 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <div>
                        <h3 className="font-bold text-[#002B5B] text-base">Partage rapide du site</h3>
                        <p className="text-xs text-gray-400">Générez des QR Codes pour vos supports imprimés ou partagez vos sites en un clic.</p>
                    </div>
                    <div className="flex flex-wrap gap-3 w-full sm:w-auto">
                        <Button
                            variant="custom"
                            className="bg-[#002B5B] hover:bg-[#001B3B] text-white flex-1 sm:flex-initial flex items-center justify-center gap-2 rounded-xl text-xs font-bold uppercase px-4 py-3"
                            onClick={() => {
                                setShareModalConfig({
                                    title: AGENT_INFO.name || 'Mon Site Agent',
                                    url: window.location.origin
                                });
                                setIsShareModalOpen(true);
                            }}
                        >
                            <QrCode size={16} /> QR Code Mon Site
                        </Button>
                        <Button
                            variant="custom"
                            className="bg-[#EA723D] hover:bg-[#d85e2b] text-white flex-1 sm:flex-initial flex items-center justify-center gap-2 rounded-xl text-xs font-bold uppercase px-4 py-3"
                            onClick={() => {
                                setShareModalConfig({
                                    title: 'Portail SCBI.EU',
                                    url: 'https://scbi.eu'
                                });
                                setIsShareModalOpen(true);
                            }}
                        >
                            <QrCode size={16} /> QR Code Portail
                        </Button>
                    </div>
                </div>

                {/* ── Titre + action ──────────────────────────────────────── */}
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
                    <div>
                        <h2 className="text-3xl font-black text-[#2C353D]">
                            Annonces MODELO <span className="text-[#EA723D] ml-2">({filteredProperties.length})</span>
                        </h2>
                        {formattedLastSync && (
                            <p className="text-xs text-gray-500 font-medium mt-1.5 flex items-center gap-2">
                                <span className="inline-block w-2 h-2 rounded-full bg-emerald-500"></span>
                                <span>Dernière synchronisation : <strong className="text-gray-700 font-bold">{formattedLastSync}</strong></span>
                                <span className="text-gray-300">|</span>
                                <span className="text-gray-400">Automatique toutes les 2h</span>
                            </p>
                        )}
                    </div>
                    <div className="flex gap-4">
                        <Button
                            onClick={handleSync}
                            disabled={syncing}
                            variant="outline"
                            className="rounded-xl border-gray-200 hover:bg-gray-50 text-gray-600 shadow-sm"
                        >
                            <RefreshCcw size={20} className={`mr-2 ${syncing ? 'animate-spin' : ''}`} />
                            {syncing ? 'Synchro...' : 'Actualiser MODELO'}
                        </Button>
                    </div>
                </div>

                {/* ── Tabs Principaux ────────────────────────────────────────────── */}
                <div className="flex gap-2 mb-3 flex-wrap">
                    {[
                        { key: 'scbi', label: `Ottange (${counts.scbi})`, color: 'bg-orange-600 text-white' },
                        { key: 'avantis', label: `Avantis (${counts.avantis})`, color: 'bg-green-600 text-white' },
                        { key: 'all', label: `Tous (${counts.all})`, color: 'bg-gray-900 text-white' },
                    ].map(tab => (
                        <button
                            key={tab.key}
                            onClick={() => handleSetDestinationFilter(tab.key)}
                            className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-wider transition-all border-2 ${destinationFilter === tab.key
                                ? `${tab.color} border-transparent shadow-md`
                                : 'bg-white text-gray-500 border-gray-200 hover:border-gray-400'
                                }`}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* ── Sous-filtre par Agent (Affiché uniquement quand Ottange est sélectionné) ────────────────── */}
                {destinationFilter === 'scbi' && availableAgentsWithCounts.length > 0 && (
                    <div className="mb-6 p-3 bg-orange-50/60 border border-orange-200 rounded-2xl">
                        <div className="flex items-center justify-between gap-2 mb-2">
                            <span className="text-[10px] font-black uppercase tracking-widest text-orange-900 flex items-center gap-1">
                                👤 Filtrer par Conseiller / Agent :
                            </span>
                            {selectedAgentFilter && (
                                <button
                                    onClick={() => setSelectedAgentFilter(null)}
                                    className="text-[9px] font-bold text-orange-600 hover:text-orange-800 hover:underline uppercase"
                                >
                                    ✕ Désélectionner
                                </button>
                            )}
                        </div>
                        <div className="flex flex-wrap gap-2">
                            {availableAgentsWithCounts.map(({ name, count }) => {
                                const isActive = selectedAgentFilter === name;
                                return (
                                    <button
                                        key={name}
                                        onClick={() => handleToggleAgentFilter(name)}
                                        className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 border ${isActive
                                            ? 'bg-orange-600 text-white border-orange-600 shadow-md ring-2 ring-orange-200'
                                            : 'bg-white text-gray-700 border-gray-200 hover:border-orange-300 hover:bg-orange-50/50'
                                            }`}
                                    >
                                        <span>👤 {name}</span>
                                        <span className={`px-1.5 py-0.2 rounded-md text-[10px] font-black ${isActive ? 'bg-white/20 text-white' : 'bg-gray-100 text-gray-500'}`}>
                                            {count}
                                        </span>
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                )}

                {/* ── Table ───────────────────────────────────────────────── */}
                {loading ? (
                    <div className="text-center py-20 text-gray-400">Récupération des données MODELO...</div>
                ) : (
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="bg-gray-50 border-b border-gray-200 text-[10px] text-gray-500 uppercase font-black tracking-widest">
                                        <th className="p-4">Bien</th>
                                        <th className="p-4">Suivi par</th>
                                        <th className="p-4">Prix</th>
                                        <th className="p-4">Ville</th>
                                        <th className="p-4">Statut</th>
                                        <th className="p-4 text-right">Visible sur Ottange</th>
                                        <th className="p-4 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {groupedItems.map(item => {
                                        if (item.isGroup) {
                                            const isExpanded = !!expandedGroups[item.key];
                                            const totalLots = item.properties.length;
                                            const onlineLots = item.properties.filter(p => p[publishedKey]).length;

                                            return (
                                                <React.Fragment key={item.key}>
                                                    {/* Parent Row (collapsible) */}
                                                    <tr 
                                                        onClick={() => toggleGroupExpand(item.key)}
                                                        className="bg-gray-50/80 hover:bg-gray-100/80 cursor-pointer select-none transition-colors border-l-4 border-[#2C353D]"
                                                    >
                                                        <td className="p-4 flex items-center gap-4">
                                                            <div className="flex items-center gap-2">
                                                                <div className="text-gray-400 p-0.5 hover:text-[#EA723D] transition-colors">
                                                                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                                                                </div>
                                                                <div className="w-16 h-12 bg-gray-200 rounded overflow-hidden flex-shrink-0 flex items-center justify-center border relative">
                                                                    {item.properties[0].image_url || item.properties[0].image ? (
                                                                        <img src={item.properties[0].image_url || item.properties[0].image} alt="" className="w-full h-full object-cover" />
                                                                    ) : (
                                                                        <span className="text-[10px] font-bold text-gray-400">PROJ</span>
                                                                    )}
                                                                    <div className="absolute top-0 left-0 bg-[#2C353D] text-[8px] font-black text-white px-1.5 py-0.5 rounded-br uppercase">PROG</div>
                                                                </div>
                                                            </div>
                                                            <div className="min-w-0">
                                                                <p className="font-extrabold text-[#2C353D] text-sm flex items-center gap-1">
                                                                    🏢 {item.title}
                                                                </p>
                                                                <div className="flex items-center gap-2 mt-0.5">
                                                                    <span className="text-[9px] bg-orange-50 text-orange-600 px-1.5 py-0.5 rounded font-black border border-orange-100 uppercase">
                                                                        Programme ({totalLots} lots)
                                                                    </span>
                                                                    <span className={`text-[9px] px-1.5 py-0.5 rounded font-black border ${onlineLots > 0 ? 'bg-green-50 text-green-600 border-green-100' : 'bg-gray-100 text-gray-500 border-gray-200'}`}>
                                                                        {onlineLots} / {totalLots} en ligne
                                                                    </span>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="p-4">
                                                            <span className="text-[9px] font-black text-gray-400 uppercase bg-gray-50 px-2 py-0.5 rounded border border-gray-150">
                                                                Multi-agents
                                                            </span>
                                                        </td>
                                                        <td className="p-4 font-black text-gray-700 text-sm whitespace-nowrap">
                                                            {item.minPrice === item.maxPrice 
                                                                ? `${new Intl.NumberFormat('fr-FR').format(item.minPrice)} €`
                                                                : `Dès ${new Intl.NumberFormat('fr-FR').format(item.minPrice)} €`
                                                            }
                                                        </td>
                                                        <td className="p-4 text-xs font-bold text-gray-500 uppercase whitespace-nowrap">{item.city}</td>
                                                        <td className="p-4">
                                                            <span className="px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest bg-gray-200 text-gray-600">
                                                                {item.status}
                                                            </span>
                                                        </td>
                                                        <td className="p-4 text-right text-xs font-bold text-[#EA723D]">
                                                            {isExpanded ? 'Replier' : 'Déplier les lots'}
                                                        </td>
                                                        <td className="p-4 text-right">
                                                            <button 
                                                                onClick={(e) => {
                                                                    e.stopPropagation();
                                                                    toggleGroupExpand(item.key);
                                                                }}
                                                                className="text-xs font-black uppercase tracking-wider text-[#002B5B] hover:text-[#EA723D] transition-colors hover:underline"
                                                            >
                                                                {isExpanded ? 'Masquer' : 'Afficher'}
                                                            </button>
                                                        </td>
                                                    </tr>

                                                    {/* Child Rows (nested properties) */}
                                                    {isExpanded && item.properties.map(property => {
                                                        const isPublished = property[publishedKey];
                                                        const isNetty = !!property.netty_ref;
                                                        const propertySyncTime = property.synced_at ? new Date(property.synced_at).getTime() : 0;
                                                        const isMissingFromFeed = isNetty && lastSyncTime && propertySyncTime < (lastSyncTime - 1000);
                                                        const displayTitle = property.title.split(' - ').slice(1).join(' - ') || property.title;

                                                        return (
                                                            <tr key={property.id} className={`transition-colors bg-gray-50/20 border-l-4 border-gray-200 ${!isPublished ? 'bg-gray-100/10 opacity-70' : 'hover:bg-orange-50/10'}`}>
                                                                
                                                                {/* Bien (indented/nested style) */}
                                                                <td className="p-4 pl-12 flex items-center gap-3">
                                                                    <div className="w-12 h-9 bg-gray-100 rounded overflow-hidden flex-shrink-0 relative border">
                                                                        <img src={property.image_url || property.image} alt="" className="w-full h-full object-cover" />
                                                                    </div>
                                                                    <div className="min-w-0">
                                                                        <p className="font-bold text-[#2C353D] truncate max-w-[200px] text-xs" title={property.title}>
                                                                            └─ {displayTitle}
                                                                        </p>
                                                                        <div className="flex items-center gap-1.5 mt-0.5">
                                                                            <span className="text-[8px] text-gray-400 font-bold uppercase">{property.type}</span>
                                                                            {property.is_favorite && <span className="text-[8px] bg-red-50 text-red-500 px-1.5 rounded font-black border border-red-100">❤️ UNE</span>}
                                                                            {isMissingFromFeed && <span className="text-[8px] bg-amber-50 text-amber-600 px-1.5 rounded font-black border border-amber-100 italic">⚠️ ABSENT</span>}
                                                                        </div>
                                                                    </div>
                                                                </td>

                                                                {/* Agent */}
                                                                <td className="p-4 pl-6">
                                                                    <div className="flex flex-col items-start gap-0.5">
                                                                        <span className="text-[9px] font-black text-[#2C353D] uppercase tracking-wider bg-gray-100 px-1.5 py-0.5 rounded border border-gray-200">
                                                                            👤 {property.agent_firstname || 'Non assigné'}
                                                                        </span>
                                                                    </div>
                                                                </td>

                                                                {/* Prix */}
                                                                <td className="p-4 font-black text-gray-600 text-xs whitespace-nowrap">
                                                                    {new Intl.NumberFormat('fr-FR').format(property.price)} €
                                                                </td>

                                                                {/* Ville */}
                                                                <td className="p-4 text-[11px] font-bold text-gray-500 uppercase whitespace-nowrap">{property.city}</td>

                                                                {/* Statut */}
                                                                <td className="p-4">
                                                                    <span className={`px-1.5 py-0.5 rounded text-[8px] font-black uppercase tracking-widest ${property.status === 'Vente' ? 'bg-[#2C353D]/80 text-white' : 'bg-[#EA723D]/80 text-white'}`}>
                                                                        {property.status}
                                                                    </span>
                                                                </td>

                                                                {/* Toggle visibilité */}
                                                                <td className="p-4 text-right">
                                                                    <div className="flex justify-end scale-90 origin-right">
                                                                        <ToggleSwitch
                                                                            active={property[publishedKey]}
                                                                            onClick={() => handleTogglePublish(property)}
                                                                        />
                                                                    </div>
                                                                </td>

                                                                {/* Actions */}
                                                                <td className="p-4 text-right">
                                                                    <div className="flex justify-end gap-1 scale-90 origin-right">
                                                                        <button onClick={() => handleToggleFavorite(property.id)} className={`p-1 rounded text-sm transition-transform active:scale-90 ${property.is_favorite ? '' : 'grayscale opacity-30 hover:opacity-100'}`} title="Mettre à la UNE">❤️</button>
                                                                        <button onClick={() => navigate(`/admin/edit/${property.id}`)} className="p-1 hover:bg-white rounded text-gray-400 hover:text-[#2C353D] transition-colors"><Edit2 size={14} /></button>
                                                                        {(!isNetty || isMissingFromFeed) && (
                                                                            <button onClick={() => handleDelete(property.id)} className="p-1 hover:bg-red-50 rounded text-gray-300 hover:text-red-500 transition-colors" title="Supprimer définitivement"><Trash2 size={14} /></button>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </React.Fragment>
                                            );
                                        } else {
                                            const { property } = item;
                                            const isPublished = property[publishedKey];
                                            const isNetty = !!property.netty_ref;
                                            const propertySyncTime = property.synced_at ? new Date(property.synced_at).getTime() : 0;
                                            const isMissingFromFeed = isNetty && lastSyncTime && propertySyncTime < (lastSyncTime - 1000);

                                            return (
                                                <tr key={property.id} className={`transition-colors ${!isPublished ? 'bg-gray-50/50' : 'hover:bg-orange-50/20'}`}>
                                                    
                                                    {/* Bien */}
                                                    <td className="p-4 flex items-center gap-4">
                                                        <div className="w-16 h-12 bg-gray-100 rounded overflow-hidden flex-shrink-0 relative border">
                                                            <img src={property.image_url || property.image} alt="" className="w-full h-full object-cover" />
                                                            <div className="absolute top-0 left-0 bg-[#2C353D] text-[8px] font-black text-white px-1.5 py-0.5 rounded-br uppercase">MODELO</div>
                                                        </div>
                                                        <div className="min-w-0">
                                                            <p className="font-bold text-[#2C353D] truncate max-w-[220px] text-sm">{property.title}</p>
                                                            <div className="flex items-center gap-2 mt-0.5">
                                                                <span className="text-[10px] text-gray-400 font-bold uppercase">{property.type}</span>
                                                                {property.is_favorite && <span className="text-[10px] bg-red-50 text-red-500 px-1.5 rounded font-black border border-red-100">❤️ À LA UNE</span>}
                                                                {isMissingFromFeed && <span className="text-[10px] bg-amber-50 text-amber-600 px-1.5 rounded font-black border border-amber-100 italic">⚠️ ABSENT DU FLUX</span>}
                                                            </div>
                                                        </div>
                                                    </td>

                                                    {/* Destination */}
                                                    <td className="p-4">
                                                        <div className="flex flex-col items-start gap-1">
                                                            <span className="text-[10px] font-black text-[#2C353D] uppercase tracking-wider bg-gray-100 px-2 py-0.5 rounded border border-gray-200">
                                                                👤 {property.agent_firstname || 'Non assigné'}
                                                            </span>
                                                            <div className="transform scale-75 origin-left opacity-60">
                                                                <DestinationBadge target={property.site_target} />
                                                            </div>
                                                        </div>
                                                    </td>

                                                    {/* Prix */}
                                                    <td className="p-4 font-black text-gray-600 text-sm whitespace-nowrap">
                                                        {new Intl.NumberFormat('fr-FR').format(property.price)} €
                                                    </td>

                                                    {/* Ville */}
                                                    <td className="p-4 text-xs font-bold text-gray-500 uppercase whitespace-nowrap">{property.city}</td>

                                                    {/* Type (Vente / Location) */}
                                                    <td className="p-4">
                                                        <span className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${property.status === 'Vente' ? 'bg-[#2C353D] text-white' : 'bg-[#EA723D] text-white'}`}>
                                                            {property.status}
                                                        </span>
                                                    </td>

                                                    {/* Toggle visibilité */}
                                                    <td className="p-4 text-right">
                                                        <div className="flex justify-end">
                                                            <ToggleSwitch
                                                                active={property[publishedKey]}
                                                                onClick={() => handleTogglePublish(property)}
                                                            />
                                                            {isMissingFromFeed && (
                                                                <div className="absolute -bottom-1 right-4 text-[7px] font-black text-amber-500 uppercase tracking-tighter">ABSENT</div>
                                                            )}
                                                        </div>
                                                    </td>

                                                    {/* Actions */}
                                                    <td className="p-4 text-right">
                                                        <div className="flex justify-end gap-1">
                                                            {property.status !== 'Location' && (
                                                                <button onClick={() => { setPosterProperty(property); setIsPosterModalOpen(true); }} className="p-2 hover:bg-blue-50 rounded text-gray-400 hover:text-blue-600 transition-colors" title="Générer Affiche Vitrine A4 / PDF"><FileText size={16} /></button>
                                                            )}
                                                            <button onClick={() => handleToggleFavorite(property.id)} className={`p-2 rounded text-base transition-transform active:scale-90 ${property.is_favorite ? '' : 'grayscale opacity-30 hover:opacity-100'}`} title="Mettre à la UNE">❤️</button>
                                                            <button onClick={() => navigate(`/admin/edit/${property.id}`)} className="p-2 hover:bg-white rounded text-gray-400 hover:text-[#2C353D] transition-colors"><Edit2 size={16} /></button>
                                                            {(!isNetty || isMissingFromFeed) && (
                                                                <button onClick={() => handleDelete(property.id)} className="p-2 hover:bg-red-50 rounded text-gray-300 hover:text-red-500 transition-colors" title="Supprimer définitivement"><Trash2 size={16} /></button>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        }
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
            <ShareModal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                propertyTitle={shareModalConfig.title}
                propertyUrl={shareModalConfig.url}
            />
            <PosterModal
                property={posterProperty}
                isOpen={isPosterModalOpen}
                onClose={() => setIsPosterModalOpen(false)}
            />
        </div>
    );
};

export default Dashboard;
